import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, filter, Subject, catchError, map } from "rxjs";
import Bugsnag from "@bugsnag/js";
import { LambdaRequest } from "@apis/_core/types/LambdaRequest";
import { PatientBase } from "@backend/graph/patients/patient-base";
import { toUnquotedJSON } from "@shared/json-utils";
import { Constants } from "projects/core/constants";
import { PatientEntry } from "../../data_model/patient";
import { validateNhsNumber } from "../../utils/nhs-number-validator";
import { filterDefined } from "../../utils/rxjs";
import { CacheService } from "../cache.service";
import { HttpService, GRAPHQL_OPERATION_NAMES } from "../http.service";
import { BaseJWTService } from "./jwt.service";
import { default as palette } from "../../../palette";
import { LocalisationService } from "../localisation.service";

export type T_Patient_Country_Fields = Pick<
  PatientBase,
  "emergency_contact_phone_country" | "gp_practice_phone_country" | "home_phone_country" | "mobile_phone_country"
>;

export enum E_PatientInfoStatus {
  SUCCESS = "SUCCESS",
  NO_CHANGE = "NO_CHANGE",
  SAVING = "SAVING",
  ERROR = "ERROR",
}

@Injectable()
export abstract class BasePatientsService {
  private _onPatientInfo = new BehaviorSubject<PatientEntry | null>(null);
  public onPatientInfoUpdated = new Subject<E_PatientInfoStatus>();
  public patientInfo: PatientEntry | null = null;
  public requestsInProgress = {
    getPatient: false,
  };
  private _palette = new Array<string>();

  constructor(
    protected _httpService: HttpService,
    protected _jwtService: BaseJWTService,
    protected _cacheService: CacheService,
    protected _localisationService: LocalisationService
  ) {
    if (!this._palette.length) this._palette = Object.keys(palette).slice(0, 6);

    this._jwtService.onJWTChanged.pipe(filter((jwt) => !LambdaRequest.patientAccessLevels.includes(jwt?.access_level))).subscribe(() => {
      this.patientInfo = null;
      this._onPatientInfo.next(null);
    });
  }

  public get isOver18(): boolean {
    return (this.patientInfo?.age ?? 0) >= 18;
  }

  public get onPatientInfo(): Observable<PatientEntry | null> {
    if (this._onPatientInfo) return this._onPatientInfo.pipe(filterDefined());
    return of(null);
  }

  public get isNHSScotland(): boolean {
    return this.patientInfo?.nhs_country === "SC";
  }

  public defaultPropertyCountryCode(property: keyof T_Patient_Country_Fields): string {
    return this.patientInfo?.[property] || this._localisationService.country_code;
  }

  protected _setPatientInfo(patient: PatientBase): void {
    this.patientInfo = new PatientEntry(Object.assign(this.patientInfo || {}, patient));

    const alphabetPosition = this.patientInfo.first_name.toLowerCase().charCodeAt(0) - 96;
    const colour = this._palette[alphabetPosition % this._palette.length];
    this.patientInfo.bg_class = `bg-${colour}-200`;
    this.patientInfo.text_class = `text-${colour}-700`;

    this._onPatientInfo.next(this.patientInfo);
  }

  protected abstract _getPatientQuery(): string;

  public async getPatient(): Promise<void> {
    try {
      this._cacheService.deleteSession(Constants.RESTRICTED_SITE_STORAGE_KEY);
      const query = this._getPatientQuery();
      const response: any = await this._httpService.query<PatientBase>(GRAPHQL_OPERATION_NAMES.PATIENT, query).pipe().toPromise();

      if (response?.data?.patient) {
        this._setPatientInfo(response.data.patient);
      } else {
        Bugsnag.notify("Patient query returned no data");
      }
    } catch (error) {
      console.error("Error in getPatient", error);
      throw error;
    }
  }

  // Only get the changed fields with the existing patient info. This will prevent unchanged fields being exposed to unauthenticated patients
  private _getUpdatedDetails(patient: Partial<PatientEntry>): Array<string> {
    const changed_fields = new Array<string>();

    for (const field in patient) {
      // Treat undefined, empty string and null as the same
      if (!patient[field] && !this.patientInfo?.[field]) continue;
      if (patient[field] !== this.patientInfo?.[field]) {
        changed_fields.push(field);
      }
    }

    const has_emergency_contact = changed_fields.includes("emergency_contact_phone") || changed_fields.includes("emergency_contact_phone_country");
    if (has_emergency_contact) changed_fields.push("emergency_contact_phone_normalized");

    return changed_fields;
  }

  public updatePatientDetails(details: Partial<PatientEntry>, fromAction: boolean): void {
    if (details.nhs_number) {
      const isValid = validateNhsNumber(details.nhs_number);
      if (!isValid) throw new Error("Invalid NHS Number");
    }

    // For each field trim the whitespace
    for (const field in details) {
      if (typeof details[field] === "string") details[field] = details[field].trim();
    }

    const changed_fields = this._getUpdatedDetails(details);
    if (!changed_fields.length) {
      this.onPatientInfoUpdated.next(E_PatientInfoStatus.NO_CHANGE);
      return;
    }

    this.onPatientInfoUpdated.next(E_PatientInfoStatus.SAVING);
    this._httpService
      .mutation<PatientBase>(
        "updatePatientDetails",
        `{
          updatePatientDetails(details: ${toUnquotedJSON(details)} fromAction: ${fromAction}) {
            details {
              ${changed_fields.join("\n")}
            }
          }
      }`
      )
      .subscribe((response) => {
        if (response.errors) {
          Bugsnag.leaveBreadcrumb("Error updating patient details", {
            patientId: this.patientInfo?.id,
            errors: response.errors,
          });
          Bugsnag.notify(response.errors);
          this.onPatientInfoUpdated.next(E_PatientInfoStatus.ERROR);
          return;
        }
        if (response?.data?.updatePatientDetails?.details) {
          this._setPatientInfo(response.data.updatePatientDetails.details);
          this.onPatientInfoUpdated.next(E_PatientInfoStatus.SUCCESS);
        }
      });
  }

  public verifyPatientDetails(details: Partial<PatientBase>): Observable<{ success: boolean; rateLimitExceeded?: boolean }> {
    return this._httpService
      .mutation<PatientBase>(
        GRAPHQL_OPERATION_NAMES.VERIFY_PATIENT_DETAILS,
        `{
        verifyPatientDetails(patient_details: ${toUnquotedJSON(details)}) {
          rateLimitExceeded
          success
          email_address
          mobile_phone
          mobile_phone_country
        }
      }`
      )
      .pipe(
        map((response) => {
          const result = response?.data?.verifyPatientDetails || { success: false };

          if (!result.success) return result;

          if (result.email_address || result.mobile_phone) {
            this._setPatientInfo({
              email_address: result.email_address,
              mobile_phone: result.mobile_phone,
              mobile_phone_country: result.mobile_phone_country,
            } as PatientBase);
          } else {
            Bugsnag.notify("Patient contact details query returned no data");
          }

          return result;
        }),
        catchError((error) => {
          Bugsnag.notify(error);
          return of({ success: false });
        })
      );
  }
}
